import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import type { ProjectView } from '@/components/ProjectViewSwitcher';

export interface ProjectPageHeaderProps {
  workspaceSlug: string;
  workspaceName: string | undefined;
  projectSlug: string;
  projectName: string | undefined;
  current: ProjectView;
}

/**
 * Glass redesign — breadcrumb-only side-effect component.
 *
 * Contributes the `workspace / project / view` breadcrumb to the
 * topbar's LEFT slot. The right slot (ViewSwitcher + per-page
 * actions) is each page's responsibility — the prototype's topbar
 * mixes workspace-wide controls (Search, Avatars, NewTask), view-
 * specific controls (Filter), and project-surface controls
 * (ViewSwitcher) in a single ordered row, which only the page knows
 * enough about to sequence correctly.
 *
 * Three breadcrumb segments per the prototype's
 * `['IT', 'app.dependable4u.com', 'Board']` shape:
 *   1. Workspace name (link to workspace home)
 *   2. Project name (link to project's List view, except when the
 *      current page IS List — then plain text)
 *   3. Current view label (plain text, `aria-current="page"`)
 */
const VIEW_LABELS: Record<ProjectView, string> = {
  list: 'List',
  board: 'Board',
  calendar: 'Calendar',
  time: 'Time',
  notes: 'Notes',
  activity: 'Activity',
  settings: 'Settings',
};

export const ProjectPageHeader = ({
  workspaceSlug,
  workspaceName,
  projectSlug,
  projectName,
  current,
}: ProjectPageHeaderProps) => {
  const projectIsCurrentPage = current === 'list';
  const viewLabel = VIEW_LABELS[current];

  const left = useMemo(
    () => (
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-[13px]"
      >
        <Link
          to="/w/$workspaceSlug"
          params={{ workspaceSlug }}
          className="text-foreground-muted hover:text-foreground transition-colors"
        >
          {workspaceName ?? 'Workspace'}
        </Link>
        <span aria-hidden="true" className="text-foreground-faint">
          /
        </span>
        {projectIsCurrentPage ? (
          <span className="text-foreground font-medium" aria-current="page">
            {projectName ?? 'Project'}
          </span>
        ) : (
          <>
            <Link
              to="/w/$workspaceSlug/p/$projectSlug"
              params={{ workspaceSlug, projectSlug }}
              className="text-foreground-muted hover:text-foreground transition-colors"
            >
              {projectName ?? 'Project'}
            </Link>
            <span aria-hidden="true" className="text-foreground-faint">
              /
            </span>
            <span className="text-foreground font-medium" aria-current="page">
              {viewLabel}
            </span>
          </>
        )}
      </nav>
    ),
    [workspaceSlug, projectSlug, workspaceName, projectName, projectIsCurrentPage, viewLabel],
  );

  useSetTopBarSlots({ left });

  // Render nothing in the page tree — the left slot does all the work.
  return null;
};
